// src/pages/PatientMedicalRecordsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  Button,
  Chip,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  CircularProgress,
  Alert,
  Tooltip,
  Stack,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  LocalHospital as DoctorIcon,
  Event as EventIcon,
  AccessTime as TimeIcon,
  Science as LabIcon,
  Image as ImageIcon,
  MedicationLiquid as MedIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import axiosInstance from '../axiosInstance';
import { toast } from 'react-toastify';

const PatientMedicalRecordsPage = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState('');
  const [records, setRecords] = useState([]);

  // Filtros
  const [query, setQuery] = useState('');
  const [fromDate, setFromDate] = useState(null); // Date object
  const [toDate, setToDate] = useState(null);     // Date object

  // Estado de detalles (reportes por consulta)
  const [loadingReportsFor, setLoadingReportsFor] = useState(null);
  const [reportsByConsulta, setReportsByConsulta] = useState({}); // { [id_consulta]: [reportes] }

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'PPP', { locale: es });
    } catch {
      return 'Fecha no disponible';
    }
  };
  const formatTime = (dateStr) => {
    try {
      return format(new Date(dateStr), 'HH:mm', { locale: es });
    } catch {
      return '--:--';
    }
  };

  const buildParams = () => {
    const params = {};
    if (query.trim()) params.search = query.trim();
    if (fromDate) params.start_date = format(fromDate, 'yyyy-MM-dd');
    if (toDate) params.end_date = format(toDate, 'yyyy-MM-dd');
    return params;
  };

  const fetchRecords = async () => {
    try {
      setLoading(true);
      setError('');
      // Nota: axiosInstance.baseURL = `${API_URL}/api`
      const { data } = await axiosInstance.get('/patients/medical-history/', {
        params: buildParams(),
      });
      setRecords(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('No se pudo cargar tu historial médico. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const reload = async () => {
    try {
      setReloading(true);
      await fetchRecords();
      toast.success('Historial actualizado');
    } finally {
      setReloading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchSubmit = (e) => {
    e?.preventDefault?.();
    fetchRecords();
  };

  const fetchReports = async (consultaId) => {
    try {
      setLoadingReportsFor(consultaId);
      const { data } = await axiosInstance.get(`/patients/medical-history/${consultaId}/reportes/`);
      const list = Array.isArray(data) ? data : [];
      setReportsByConsulta((prev) => ({ ...prev, [consultaId]: list }));
      if (list.length === 0) {
        toast.info('Esta consulta no tiene reportes adjuntos.');
      }
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los reportes adjuntos.');
    } finally {
      setLoadingReportsFor(null);
    }
  };

  const viewPrescriptionPDF = async (consultaId) => {
    try {
      // Obtener PDF como blob para preservar el token
      const url = `/patients/medical-history/${consultaId}/receta/`;
      const response = await axiosInstance.get(url, { responseType: 'blob' });
      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error(e);
      toast.error('No se pudo abrir la receta. Intenta nuevamente.');
    }
  };

  const filteredRecords = useMemo(() => {
    // El servidor ya filtra, esto es una capa adicional rápida en el cliente
    return records;
  }, [records]);

  const EmptyState = () => (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Typography variant="h6" color="text.primary" gutterBottom>
        No hay registros médicos que coincidan con los filtros
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Ajusta la búsqueda o el rango de fechas para ver más resultados.
      </Typography>
    </Box>
  );

  const RecordCard = ({ record }) => {
    const consultaId = record.id_consulta;

    const hasMeds = Array.isArray(record.medicamentos) && record.medicamentos.length > 0;
    const hasLabs = !!record.examenes_laboratoriales;
    const hasImages = !!record.examenes_imagen;

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderRadius: 3,
          border: 'none',
          boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
          },
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md="auto">
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, width: 56, height: 56 }}>
                <DescriptionIcon />
              </Avatar>
            </Grid>

            <Grid item xs>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {record.diagnostico_principal}
              </Typography>

              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <DoctorIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {record.medico_nombre}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <EventIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {formatDate(record.fecha_hora_consulta)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TimeIcon color="action" fontSize="small" />
                  <Typography variant="body2" color="text.secondary">
                    {formatTime(record.fecha_hora_consulta)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                {hasMeds && <Chip icon={<MedIcon />} label="Con medicación" size="small" color="primary" variant="outlined" />}
                {hasLabs && <Chip icon={<LabIcon />} label="Exámenes de laboratorio" size="small" color="secondary" variant="outlined" />}
                {hasImages && <Chip icon={<ImageIcon />} label="Exámenes de imagen" size="small" variant="outlined" />}
              </Stack>
            </Grid>
          </Grid>

          {/* Medicamentos (si existen) */}
          {hasMeds && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Medicación prescrita
              </Typography>
              <Grid container spacing={1}>
                {record.medicamentos.map((m) => (
                  <Grid item xs={12} md={6} key={`${m.nombre}-${m.dosis}-${m.id_medicamento || Math.random()}`}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {m.nombre} {m.nombre_comercial ? `(${m.nombre_comercial})` : ''} — {m.dosis || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {m.frecuencia || '-'} {m.duracion ? `• ${m.duracion}` : ''}
                      </Typography>
                      {m.notas && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Indicaciones: {m.notas}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Reportes adjuntos */}
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap">
              <Typography variant="subtitle2" color="text.secondary">
                Archivos y Recetas
              </Typography>

              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: { xs: 1, sm: 0 } }}>
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={() => viewPrescriptionPDF(consultaId)}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Ver receta (PDF)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExpandMoreIcon />}
                  onClick={() => fetchReports(consultaId)}
                  disabled={loadingReportsFor === consultaId}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  {loadingReportsFor === consultaId ? 'Cargando...' : 'Ver reportes'}
                </Button>
              </Stack>
            </Stack>

            {/* Lista de reportes (si se cargaron) */}
            {reportsByConsulta[consultaId] && (
              reportsByConsulta[consultaId].length > 0 ? (
              <Grid container spacing={1} sx={{ mt: 1 }}>
                {reportsByConsulta[consultaId].map((r) => (
                  <Grid item xs={12} md={6} key={r.id_reporte}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        bgcolor: alpha(theme.palette.background.default, 0.6),
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36 }}>
                          <DescriptionIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ maxWidth: 260 }} noWrap title={r.nombre_archivo || 'Reporte'}>
                            {r.nombre_archivo || 'Reporte'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(r.fecha_subida)} {formatTime(r.fecha_subida)}
                          </Typography>
                        </Box>
                      </Stack>
                      <Tooltip title="Abrir">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => {
                              // r.archivo es una URL absoluta si el backend usa FileField(use_url=True)
                              if (r.archivo) window.open(r.archivo, '_blank', 'noopener,noreferrer');
                            }}
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No hay archivos adjuntos para esta consulta.
              </Typography>
            )
          )}    
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Encabezado y filtros */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 4,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.06)}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Stack direction="row" spacing={2} alignItems="center">
               <Avatar
               sx={{
                bgcolor: theme.palette.primary.main,
                width: 56,
                height: 56,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}  
            >
              <DescriptionIcon sx={{ color: 'white' }} />
            </Avatar>  
            <Typography variant="h5" fontWeight="bold">
              Historial Médico
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Consulta tus atenciones, recetas y archivos adjuntos
            </Typography>
             </Stack>
          </Grid>

          <Grid item xs={12} md={8}>
            <Box component="form" onSubmit={handleSearchSubmit}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <TextField
                    placeholder="Buscar por diagnóstico, médico..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    fullWidth
                    InputProps={{
                      startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <DatePicker
                    label="Desde"
                    value={fromDate}
                    onChange={(v) => setFromDate(v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={6} sm={2.5}>
                  <DatePicker
                    label="Hasta"
                    value={toDate}
                    onChange={(v) => setToDate(v)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{ textTransform: 'none', borderRadius: 2, whiteSpace: 'nowrap' }}
                  >
                    Filtrar
                  </Button>
                  <Tooltip title="Actualizar">
                    <span>
                      <IconButton onClick={reload} disabled={reloading}>
                        {reloading ? <CircularProgress size={22} /> : <RefreshIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Contenido */}
      {loading ? (
        <Box sx={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : filteredRecords.length === 0 ? (
        <EmptyState />
      ) : (
        <Box>
          {filteredRecords.map((rec) => (
            <RecordCard key={rec.id_consulta} record={rec} />
          ))}
        </Box>
      )}
    </Container>
  );
};

export default PatientMedicalRecordsPage;
